/**
 * PhotoRender Component
 * 
 * Displays a rendered photo using the RenderService.
 * Shows loading states, progressive updates, and errors.
 */

import RenderService from './render_service.js';

export class PhotoRender {
  constructor(container, options = {}) {
    this.container = container;
    this.photoId = options.photoId;
    
    // Render settings (all passed to RenderService)
    this.settings = {
      style: options.style || 'hta',
      resolution: options.resolution || 512,
      num_inference_steps: options.numInferenceSteps || 28,
      cfg_scale: options.style_strength || 2.0,
      control_scale: options.controlScale || 1.0,
      seed: options.seed || Math.floor(Math.random() * 100000),
      base_shift: options.baseShift !== undefined ? options.baseShift : 0.5,
      max_shift: options.maxShift !== undefined ? options.maxShift : 1.15,
      sample_every: options.sampleEvery !== undefined ? options.sampleEvery : 1,
      jpg_quality: options.jpgQuality || 85
    };
    
    // State
    this.currentBlobUrl = null;
    this.isLoading = false;
    this.isComplete = false;
    this.error = null;
    this.cancelRender = null;
    
    // Callbacks
    this.onProgressCallback = options.onProgress || (() => {});
    this.onCompleteCallback = options.onComplete || (() => {});
    this.onErrorCallback = options.onError || (() => {});
    
    this.render();
  }

  render() {
    this.container.innerHTML = `
      <div class="photo-render relative w-full h-full flex items-center justify-center bg-gray-900">
        <div class="photo-render-content w-full h-full flex items-center justify-center">
          <div class="photo-render-loading text-white">
            <svg class="animate-spin h-12 w-12 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-center">Generating...</p>
          </div>
          <img class="photo-render-image hidden max-w-full max-h-full object-contain" alt="Rendered photo" />
          <div class="photo-render-error hidden text-white text-center">
            <svg class="h-12 w-12 mx-auto mb-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
            <p class="photo-render-error-message"></p>
          </div>
        </div>
      </div>
    `;
    
    this.loadingEl = this.container.querySelector('.photo-render-loading');
    this.imageEl = this.container.querySelector('.photo-render-image');
    this.errorEl = this.container.querySelector('.photo-render-error');
    this.errorMessageEl = this.container.querySelector('.photo-render-error-message');
    
    this.startRender();
  }

  async startRender() {
    this.isLoading = true;
    this.isComplete = false;
    this.showLoading();
    
    // Request render from service
    this.cancelRender = RenderService.getPhoto(
      this.photoId,
      this.settings,
      {
        onProgress: (imageData) => {
          this.handleProgress(imageData);
        },
        onError: (error) => {
          this.handleError(error);
        },
        onComplete: (imageData) => {
          this.handleComplete(imageData);
        }
      }
    );
  }

  handleProgress(imageData) {
    // Convert Uint8Array to Blob and create URL
    const blob = new Blob([imageData], { type: 'image/jpeg' });
    const imageUrl = URL.createObjectURL(blob);
    
    // Revoke old blob URL
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
    }
    
    this.currentBlobUrl = imageUrl;
    this.showImage(imageUrl);
    
    // Call user callback
    this.onProgressCallback(imageUrl);
  }

  handleComplete(imageData) {
    this.isLoading = false;
    this.isComplete = true;
    
    // Final image already displayed via onProgress
    // Just call user callback
    if (this.currentBlobUrl) {
      this.onCompleteCallback(this.currentBlobUrl);
    }
  }

  handleError(error) {
    this.isLoading = false;
    this.error = error.message;
    this.showError(error.message);
    this.onErrorCallback(error);
  }

  showLoading() {
    this.loadingEl.classList.remove('hidden');
    this.imageEl.classList.add('hidden');
    this.errorEl.classList.add('hidden');
  }

  showImage(imageUrl) {
    this.loadingEl.classList.add('hidden');
    this.imageEl.classList.remove('hidden');
    this.errorEl.classList.add('hidden');
    this.imageEl.src = imageUrl;
  }

  showError(message) {
    this.loadingEl.classList.add('hidden');
    this.imageEl.classList.add('hidden');
    this.errorEl.classList.remove('hidden');
    this.errorMessageEl.textContent = message;
  }

  updateParams(params) {
    // Map incoming params to settings keys
    const paramMapping = {
      style: 'style',
      resolution: 'resolution',
      numInferenceSteps: 'num_inference_steps',
      style_strength: 'cfg_scale',
      controlScale: 'control_scale',
      seed: 'seed',
      baseShift: 'base_shift',
      maxShift: 'max_shift',
      sampleEvery: 'sample_every',
      jpgQuality: 'jpg_quality'
    };
    
    // Check if any settings actually changed
    let hasChanges = false;
    for (const [paramKey, settingKey] of Object.entries(paramMapping)) {
      if (params[paramKey] !== undefined && this.settings[settingKey] !== params[paramKey]) {
        this.settings[settingKey] = params[paramKey];
        hasChanges = true;
      }
    }
    
    // Only cancel and restart if something actually changed
    if (hasChanges) {
      this.cancel();
      this.startRender();
    }
  }

  cancel() {
    if (this.cancelRender) {
      console.log(`[PhotoRender] Canceling render for photo ${this.photoId}`, {
        settings: this.settings,
        isComplete: this.isComplete
      });
      this.cancelRender();
      this.cancelRender = null;
    }
  }

  destroy() {
    // Only cancel if render is not complete
    // Complete renders should stay in cache
    if (!this.isComplete) {
      console.log(`[PhotoRender] Destroying incomplete render for photo ${this.photoId}`);
      this.cancel();
    }
    
    // Revoke blob URL
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
      this.currentBlobUrl = null;
    }
    
    this.container.innerHTML = '';
  }
}

// Static method to clear cache
PhotoRender.clearCache = async () => {
  await RenderService.clearCache();
};

// Static method to get cache stats
PhotoRender.getCacheStats = async () => {
  return await RenderService.getCacheStats();
};

export default PhotoRender;
